import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

export const StatsSection: React.FC<{ variant?: 'light' | 'dark' }> = ({ variant = 'light' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [count, setCount] = useState(0);

  const isDark = variant === 'dark';
  
  // Define styles based on variant
  const bgC = isDark ? 'bg-black' : 'bg-white';
  const textTitle = isDark ? '#FFFFFF' : '#6B0D3E';
  
  // Capsule styles
  const capsuleBorder = isDark 
    ? { border: '1px solid rgba(255, 77, 158, 0.2)', bg: 'bg-[#0a0a0a]', shadow: 'none' }
    : { border: '1px solid #FFD3EA', bg: 'bg-white', shadow: '0 4px 15px rgba(255, 211, 234, 0.3)' };
    
  const capsuleGradient = isDark
    ? { background: 'linear-gradient(90deg, #3D0C23 0%, #1A0510 100%)' }
    : { background: 'linear-gradient(60.98deg, #FFBFE0 27.81%, #FDE6F2 72.19%)' };

  // const capsuleText = isDark ? 'text-gray-300' : 'text-gray-700';

  // Counting animation
  useEffect(() => {
    if (isInView && count < 6) {
      const timer = setTimeout(() => {
        setCount(count + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isInView, count]);

  return (
    <section className={`py-20 ${bgC} relative overflow-hidden`} ref={ref}>
      {/* Floating background elements - Hide or Adjust for Dark Mode */}
      {!isDark && [...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full opacity-5"
          style={{
            width: `${50 + Math.random() * 100}px`,
            height: `${50 + Math.random() * 100}px`,
            background: 'linear-gradient(135deg, #FFBFE0, #FDE6F2)',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -40, 0],
            x: [0, 30, 0],
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 10 + i * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5
          }}
        />
      ))}

      <div className="w-full px-4 relative z-10">
        {/* Heading */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 
            className="font-heading font-bold"
            style={{
              fontFamily: 'Nanum Myeongjo',
              fontSize: 'clamp(28px, 4vw, 48px)',
              lineHeight: '1.3',
              color: textTitle,
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{ display: 'inline-block' }}
            >
              Good packaging stands out. 📦
            </motion.span>
            <br />
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              style={{ display: 'inline-block' }}
            >
              Great packaging simply belongs. 🍃
            </motion.span>
          </motion.h2>
        </motion.div>

        {/* First Row - Responsive */}
        <div className="w-full max-w-7xl mx-auto mb-4 md:mb-6">
          <div className="flex flex-col md:grid md:grid-cols-10 gap-3 md:gap-4 items-center">
            {/* 1st Capsule */}
            <motion.div
              className={`w-full md:col-span-3 rounded-full px-4 md:px-8 py-4 md:py-6 shadow-lg ${capsuleBorder.bg} flex items-center justify-center relative overflow-hidden`}
              style={{
                border: capsuleBorder.border,
                minHeight: '80px',
                boxShadow: capsuleBorder.shadow !== 'none' ? capsuleBorder.shadow : undefined
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { 
                opacity: 1, 
                scale: 1, 
                y: [0, -8, 0],
              } : { y: [0, -8, 0] }}
              transition={{
                opacity: { duration: 0.6, delay: 0.6 },
                scale: { duration: 0.6, delay: 0.6 },
                y: { duration: 2.5, repeat: Infinity, ease: "easeInOut" }
              }}
            >
              <div className="flex gap-2 opacity-50">
                   {[...Array(3)].map((_, i) => (
                    <img 
                        key={i}
                        src="/Images/star-border.webp"
                        className={`w-8 h-8 md:w-12 md:h-12 ${isDark ? 'invert opacity-50' : ''}`}
                        alt="Star"
                    />
                   ))}
              </div>
            </motion.div>

            {/* 2nd Capsule - Gradient */}
            <motion.div
              className="w-full md:col-span-4 rounded-full px-4 md:px-16 py-4 md:py-6 shadow-xl relative overflow-hidden flex items-center justify-center"
              style={{
                ...capsuleGradient,
                minHeight: '80px'
              }}
              initial={{ opacity: 0, y: -50 }}
              animate={isInView ? { opacity: 1, y: [0, -10, 0] } : { y: [0, -10, 0] }}
              transition={{
                opacity: { duration: 0.8, delay: 0.7 },
                y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
              }}
            >
              <div className="relative z-10 flex items-center justify-center gap-3 md:gap-6">
                <motion.div
                  className="text-4xl md:text-6xl font-bold"
                  style={{
                    color: '#FF4D9E', 
                  }}
                >
                  {count > 0 ? count : ''}%
                </motion.div>
                <div className="text-left">
                  <motion.p 
                    className={`${isDark ? 'text-gray-200' : 'text-gray-700'} text-xs md:text-sm font-medium leading-tight`}
                  >
                    Our users' average
                    <br />
                    saving amount
                  </motion.p>
                </div>
              </div>
            </motion.div>

            {/* 3rd Capsule */}
            <motion.div
              className={`w-full md:col-span-3 rounded-full px-4 md:px-8 py-4 md:py-6 shadow-lg ${capsuleBorder.bg} flex items-center justify-center relative overflow-hidden`}
               style={{
                border: capsuleBorder.border,
                minHeight: '80px',
                boxShadow: capsuleBorder.shadow !== 'none' ? capsuleBorder.shadow : undefined
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { 
                opacity: 1, 
                scale: 1, 
                y: [0, -8, 0],
              } : { y: [0, -8, 0] }}
              transition={{
                opacity: { duration: 0.6, delay: 0.6 },
                scale: { duration: 0.6, delay: 0.6 },
                y: { duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
              }}
            >
               <div className="flex gap-2">
                   {[...Array(3)].map((_, i) => (
                    <img 
                        key={i}
                        src="/Images/star-border.webp"
                        className={`w-8 h-8 md:w-12 md:h-12 ${isDark ? 'invert opacity-50' : ''}`}
                        alt="Star"
                    />
                   ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Second Row - Responsive */}
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:grid gap-3 md:gap-4 items-center" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {/* 4th Capsule - Gradient */}
            <motion.div
              className={`w-full rounded-full px-4 md:px-12 py-4 md:py-5 shadow-lg relative overflow-hidden flex items-center justify-center`}
               style={{
                ...capsuleGradient,
                minHeight: '80px'
              }}
              initial={{ opacity: 0, x: -100 }}
              animate={isInView ? { opacity: 1, x: 0, y: [0, -8, 0] } : { y: [0, -8, 0] }}
              transition={{
                opacity: { duration: 0.8, delay: 0.9 },
                x: { duration: 0.8, delay: 0.9, type: "spring", stiffness: 100 },
                y: { duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }
              }}
            >
              <div className="relative z-10 flex items-center justify-center gap-3 md:gap-4">
                <div className="text-3xl md:text-5xl font-bold" style={{ color: '#FF4D9E' }}>
                  {count > 0 ? count : ''}%
                </div>
                <div className="text-left">
                  <motion.p 
                    className={`${isDark ? 'text-gray-200' : 'text-gray-700'} text-[10px] md:text-xs font-medium leading-tight`}
                  >
                    Our users' average
                    <br />
                    saving amount
                  </motion.p>
                </div>
              </div>
            </motion.div>

            {/* 5th Capsule - Center Star */}
            <motion.div
              className={`w-full rounded-full px-4 md:px-8 py-4 md:py-6 shadow-lg ${capsuleBorder.bg} flex items-center justify-center relative`}
               style={{
                border: capsuleBorder.border,
                minHeight: '80px',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={isInView ? { 
                opacity: 1, 
                scale: 1,
              } : {}}
              transition={{
                opacity: { duration: 0.8, delay: 1.0 },
                scale: { duration: 0.8, delay: 1.0, type: "spring", stiffness: 200 },
              }}
            >
               <img src="/Images/star_filled.webp" className={`w-12 h-12 md:w-16 md:h-16 ${isDark ? 'invert' : ''}`} alt="Star" />
            </motion.div>

            {/* 6th Capsule - Gradient */}
            <motion.div
               className={`w-full rounded-full px-4 md:px-12 py-4 md:py-5 shadow-lg relative overflow-hidden flex items-center justify-center`}
               style={{
                ...capsuleGradient,
                minHeight: '80px'
              }}
              initial={{ opacity: 0, x: 100 }}
              animate={isInView ? { opacity: 1, x: 0, y: [0, -8, 0] } : { y: [0, -8, 0] }}
              transition={{
                opacity: { duration: 0.8, delay: 0.9 },
                x: { duration: 0.8, delay: 0.9, type: "spring", stiffness: 100 },
                y: { duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1 }
              }}
            >
              <div className="relative z-10 flex items-center justify-center gap-3 md:gap-4">
               <div className="text-3xl md:text-5xl font-bold" style={{ color: '#FF4D9E' }}>
                  {count > 0 ? count : ''}%
                </div>
                <div className="text-left">
                  <motion.p 
                    className={`${isDark ? 'text-gray-200' : 'text-gray-700'} text-[10px] md:text-xs font-medium leading-tight`}
                  >
                    Our users' average
                    <br />
                    saving amount
                  </motion.p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

